// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ButtonIcon } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TippyPopupStyle } from "@turnerBlueprint/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import HeaderClickable from "@turnerBlueprint/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@turnerBlueprint/App/Components/MiniMyAccount/MiniMyAccount";
import TippyPopup from "@turnerBlueprint/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";
import IconHeaderAccount from "@turnerBlueprint/XDL/Icons/IconHeaderAccount";
import IconHeaderSignin from "@turnerBlueprint/XDL/Icons/IconHeaderSignin";
import IconMenuOpen from "@turnerBlueprint/XDL/Icons/IconMenuOpen";
import { color, triggerEsc } from "@turnerBlueprint/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /* <XNG-Changes> */
    accountAndOrderManagementTitle = "accountAndOrderManagementTitle",
    accountAndOrderManagementLinks = "accountAndOrderManagementLinks",
}

/* <XNG-Changes> */
export interface MyAccountExtraLinks {
    fields: {
        destination: LinkFieldValue;
        overrideTitle: string;
    };
}
/* <XNG-Changes> */
export interface MappedLink {
    title: string;
    url: string;
    type?: string;
}

/* <XNG-Changes> */
export const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return (
        widgetLinks
            ?.map(widgetLink => {
                if (widgetLink.fields.destination.type === "Page") {
                    const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                    if (pageLink) {
                        return {
                            ...pageLink,
                            title: widgetLink.fields.overriddenTitle || pageLink?.title,
                            openInNewWindow: widgetLink.fields.openInNewWindow,
                        };
                    }
                } else if (widgetLink.fields.destination.type === "Url") {
                    const url = widgetLink.fields.destination.value;
                    const title = widgetLink.fields.overriddenTitle;

                    return { id: "", url, title };
                }
                return undefined;
            })
            ?.filter(pageLink =>
                (pageLink as any)?.parentId === myAccountPage?.id ? !(pageLink as any)?.excludeFromNavigation : true,
            ) || []
    );
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const links = mapLinks(state, ownProps.fields.accountAndOrderManagementLinks);
    const linkGroups = [{ title: ownProps.fields.accountAndOrderManagementTitle, links }];
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        isAuthenticated: xIsAuthenticated(state),
        linkGroups,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    tippyPopup?: TippyPopupStyle;
    signInDrawer?: DrawerPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase;
        `,
    },
    tippyPopup: {
        main: {
            css: css`
                &.tippy-box > .tippy-content {
                    padding: 0;
                }
            `,
        },
    },
    signInDrawer: {
        size: 335,
        position: "right",
        cssOverrides: {
            drawerBody: css`
                padding: 16px;
                border: 1px solid ${color("common.ui4")};
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
            `,
            drawerContent: css`
                box-shadow: none;
                overflow: visible;
                width: 100%;
            `,
            drawerTitle: css`
                background: none;
                padding: 0;
                min-height: 0;
            `,
        },
        closeButtonIconProps: {
            src: IconMenuOpen,
            size: 14,
            color: "common.ui5",
            css: css`
                &:hover {
                    &,
                    & svg {
                        color: ${color("common.ui9")};
                    }
                }
            `,
        },
        closeButtonProps: {
            css: css`
                &,
                &:not(:disabled):hover {
                    background: none;
                    padding: 0;
                    min-height: 0;
                    position: absolute;
                    right: 16px;
                    top: 16px;
                }

                ${ButtonIcon} {
                    color: ${color("common.ui5")};
                }
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        /* <XNG-Changes> */
        [fields.accountAndOrderManagementTitle]: string;
        [fields.accountAndOrderManagementLinks]: LinkModel[];
    };
    isCompactHeaderSingIn?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    id,
    linkGroups,
    isTabletScreen,
    isMobileScreen,
    isAuthenticated,
}: Props) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    // <XNG-Changes>
    const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState(false);

    useEffect(() => {
        setIsSignInDrawerOpen(false);
    }, [currentLocation]);
    // </XNG-Changes>

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = isAuthenticated ? IconHeaderAccount : IconHeaderSignin;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    // let menuItems = (myAccountPageLink?.children?.filter(
    //     pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    // ) ?? []) as MappedLink[];

    let menuItems = myAccountPageLink?.children?.filter(pageLink => !pageLink.excludeFromNavigation);
    // const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
    //     return {
    //         title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
    //         iconProps: {
    //             src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
    //             size: 22,
    //             css: css`
    //                 margin-left: 20px;
    //                 margin-right: -12px;
    //             `,
    //         },
    //         clickableProps: {
    //             css: css`
    //                 display: flex;
    //                 text-transform: uppercase;
    //             `,
    //             onClick: () => {
    //                 title === "Inventory"
    //                     ? setShowInventory({ showInventory: !showInventory })
    //                     : setShowPricing({ showPricing: !showPricing });
    //             },
    //         },
    //     } ;
    // };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        // showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        // showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        e.preventDefault();

        if (isAuthenticated) {
            // XNG-CHANGES : To make sure all tippy are hidden and scrollbar is present
            hideAll();
            history.push(myAccountPageLink?.url || "/MyAccount");
        } else if (isTabletScreen) {
            history.push(signInUrl || "/MyAccount/SignIn");
        } else {
            setIsSignInDrawerOpen(true);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = translate("My Account");
    }

    // XNG-Changes
    const signInClickable = (
        <HeaderClickable
            iconProps={{ src: icon }}
            onClick={onSignInHandler}
            title={signInStatusText}
            isOpen={isPopupShown}
            showArrow={!!isAuthenticated}
        />
    );

    if (isTabletScreen) {
        return signInClickable;
    }

    if (!userName || (userName && currentUserIsGuest)) {
        return (
            <>
                {signInClickable}
                <Drawer
                    position="right"
                    {...styles.signInDrawer}
                    isOpen={isSignInDrawerOpen}
                    handleClose={() => setIsSignInDrawerOpen(false)}
                >
                    <XngZone contentId={id} zoneName="content0" />
                </Drawer>
            </>
        );
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            maxWidth={430}
            content={<MiniMyAccount linkGroups={linkGroups} onSignOutHandler={onSignOutHandler} />}
            arrow={false}
            offset={BLUEPRINT_NAME === "turnerBlueprint" ? [0, 19] : [0, 22]}
            placement="bottom-end"
            type="menu"
            onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
            showCloseButton={false}
            onShow={instance => {
                hideAll();
                triggerEsc();
            }}
        >
            {signInClickable}
        </TippyPopup>
    );
};

export const linksFieldDefinitions = [
    {
        name: fields.accountAndOrderManagementTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Account & Order Management",
    },
    {
        displayName: "Links",
        name: fields.accountAndOrderManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.includeAccountMenu,
            //     displayName: "Include My Account Menu",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
            // XNG-Changes
            ...linksFieldDefinitions,
        ] as FieldDefinition[],
        group: "Common",
    },
};

export default widgetModule;
